import type { PartialDeep } from 'type-fest';
import { isAnyOf } from '@reduxjs/toolkit';

import { Lead } from '@backend/src/interfaces/mongoose.gen';
import { RootState, AppDispatch } from 'store/configureStore';
import createAppSlice from 'store/createAppSlice';
import backendApi from 'slices/backendApi';
import { apiSubmit } from 'actions/leadActions';

export type UnsavedLeadState = {
  lead: PartialDeep<Lead> | undefined;
  isOnline: boolean;
};

const initialState: UnsavedLeadState = { lead: undefined, isOnline: true };

export const unsavedLeadSlice = createAppSlice({
  name: 'unsavedLead',
  initialState,
  reducers: (create) => ({
    updateUnsavedLead: create.reducer<PartialDeep<Lead>>((state, action) => {
      state.lead = { ...(state.lead || {}), ...action.payload };
    }),
    updateOnlineStatus: create.reducer<boolean>((state, action) => {
      state.isOnline = action.payload;
    }),
    triggerOnlineStatusChange: create.asyncThunk(
      async (newStatus: boolean, thunkApi) => {
        const oldState = thunkApi.getState() as RootState;
        const dispatch = thunkApi.dispatch as AppDispatch;
        dispatch(unsavedLeadSlice.actions.updateOnlineStatus(newStatus));

        // The app was offline but is now online, and has unsaved field, so try submitting them now.
        if (
          newStatus === true &&
          oldState.unsavedLead.isOnline === false &&
          oldState.unsavedLead.lead
        ) {
          dispatch(apiSubmit(oldState.unsavedLead.lead || {}));
        }
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(backendApi.endpoints.createLead.matchFulfilled),
      (state) => {
        state.lead = undefined;
      },
    );
    builder.addMatcher(
      isAnyOf(backendApi.endpoints.updateLead.matchFulfilled),
      (state) => {
        state.lead = undefined;
      },
    );
  },
});

// Action creators are generated for each case reducer function
export const { updateUnsavedLead, triggerOnlineStatusChange } =
  unsavedLeadSlice.actions;

export default unsavedLeadSlice;
